const DEFAULT_ACCENT = '#84cc16';
const DARK_FOREGROUND = '#0f172a';
const LIGHT_FOREGROUND = '#ffffff';

type Rgb = { r: number; g: number; b: number };

function parseHexColor(value: string): Rgb | null {
  const hex = value.trim().replace(/^#/, '');
  if (![3, 4, 6, 8].includes(hex.length) || !/^[0-9a-f]+$/i.test(hex)) return null;

  const normalized =
    hex.length <= 4
      ? hex
          .slice(0, 3)
          .split('')
          .map((character) => character + character)
          .join('')
      : hex.slice(0, 6);

  return {
    r: Number.parseInt(normalized.slice(0, 2), 16),
    g: Number.parseInt(normalized.slice(2, 4), 16),
    b: Number.parseInt(normalized.slice(4, 6), 16),
  };
}

function relativeLuminance({ r, g, b }: Rgb): number {
  const channels = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });

  const [red = 0, green = 0, blue = 0] = channels;
  return red * 0.2126 + green * 0.7152 + blue * 0.0722;
}

function contrastRatio(first: number, second: number): number {
  const lighter = Math.max(first, second);
  const darker = Math.min(first, second);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Normalise l'accent public et choisit automatiquement un texte clair ou
 * sombre. Le couple retourné maximise le contraste des CTA personnalisés.
 */
export function storefrontTheme(accent: string | null | undefined): {
  accent: string;
  foreground: string;
  contrast: number;
} {
  const color = accent?.trim() || DEFAULT_ACCENT;
  const rgb = parseHexColor(color) ?? (parseHexColor(DEFAULT_ACCENT) as Rgb);
  const luminance = relativeLuminance(rgb);
  const darkContrast = contrastRatio(
    luminance,
    relativeLuminance(parseHexColor(DARK_FOREGROUND) as Rgb),
  );
  const lightContrast = contrastRatio(luminance, 1);
  const useDark = darkContrast >= lightContrast;

  return {
    accent: parseHexColor(color) ? color : DEFAULT_ACCENT,
    foreground: useDark ? DARK_FOREGROUND : LIGHT_FOREGROUND,
    contrast: useDark ? darkContrast : lightContrast,
  };
}
